import type { FormElement, TextInputProperties } from '../../model/form.types';
import type { FieldDefinition, ValidationIssue } from '../../model/types';
import { defaultPatternTest } from '../pattern-test';
import { builderDefaultText, isEmptyValue, maxLengthIssue, requiredIssue, singleColumn, stringShapeIssue, valueAsText } from './shared';

type TextInputElement = FormElement & { properties: TextInputProperties };

// The author's regex runs on every save, and a pattern such as /^(a+)+$/ backtracks
// exponentially with the input length, so no answer longer than the limit reaches it. The value
// matches the builder's limit for the default value ("Valor"), so every default the builder
// accepts still validates, and stays above the 100 the builder allows for the author's own
// maximum. It only bounds the input; a catastrophic pattern can still backtrack within it.
export const TEXT_ANSWER_MAX_LENGTH = 200;

// The configuration panel shows the same text under "Valor", so the author and the person filling
// the record read one sentence for one rule.
export function textLengthRangeMessage(minLength?: number, maxLength?: number): string {
  if (minLength !== undefined && maxLength !== undefined) {
    return `O valor deve ter entre ${minLength} e ${maxLength} caracteres.`;
  }
  if (minLength !== undefined) return `O valor deve ter no mínimo ${minLength} caracteres.`;
  return `O valor deve ter no máximo ${maxLength} caracteres.`;
}

export const textInputDefinition: FieldDefinition<TextInputElement> = {
  kind: 'field',
  type: 'TEXT_INPUT',
  emptyValue: builderDefaultText,
  control: (element) => ({
    kind: 'text',
    placeholder: element.properties.placeholder,
    minLength: element.properties.minLength,
    maxLength: element.properties.maxLength ?? TEXT_ANSWER_MAX_LENGTH,
    regex: element.properties.regex,
  }),
  shapeIssue: stringShapeIssue,
  validate: (element, value, context) => {
    const shape = stringShapeIssue(element, value);
    if (shape) return [shape];
    const issues: ValidationIssue[] = [];
    const text = typeof value === 'string' ? value : '';
    if (isEmptyValue(text)) {
      if (element.properties.required) issues.push(requiredIssue(element));
      return issues;
    }
    const { minLength, maxLength: authorMaxLength, regex } = element.properties;
    if (authorMaxLength !== undefined && text.length > authorMaxLength) {
      return [{ fieldId: element.id, code: 'maxLength', message: textLengthRangeMessage(minLength, authorMaxLength) }];
    }
    if (text.length > TEXT_ANSWER_MAX_LENGTH) return [maxLengthIssue(element, TEXT_ANSWER_MAX_LENGTH)];
    if (minLength !== undefined && text.length < minLength) {
      issues.push({ fieldId: element.id, code: 'minLength', message: textLengthRangeMessage(minLength, authorMaxLength) });
    }
    const patternTest = context?.patternTest ?? defaultPatternTest;
    if (regex && regex.trim().length > 0 && !patternTest(regex, text)) {
      issues.push({ fieldId: element.id, code: 'regex', message: 'Formato inválido.' });
    }
    return issues;
  },
  columns: singleColumn,
  cells: (_element, value) => [valueAsText(value)],
  triggerMatches: () => false,
};
